import { useEffect, useState, type CSSProperties } from 'react'
import { TraceRegion } from '../lib/trace'
import { api, type Child } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button } from './ui'
import {
  COLOR_GROUPS, DEFAULT_BUDDY, HELD, LABELS, LEAF_GROUPS, LIMB_GROUPS, SproutBuddy, armsFor, buddyCode,
  holdsSomething, material, parseBuddy, randomBuddy, type Buddy,
} from './SproutBuddy'
import { sfx } from '../lib/sound'
import { english, t } from '../lib/i18n'

/**
 * Make your own Sprout Buddy (8 Oct 2026).
 *
 * The third way to choose a picture, next to the drawn pictures and a photo,
 * and the one a child can do for themselves. Four parts, a big live preview,
 * and a "surprise me" for anyone who would rather not choose. With nearly
 * thirty colours and fourteen poses (9 Oct), the choices come in small
 * labelled groups (gems, metals, space; winning, showing off) so a child can
 * find "the space ones" without reading every name.
 *
 * **Laid out to need no scrolling** (Sam, 9 Oct). The buddy sits top left,
 * and beside it two columns of tabs: the part (Top, Skin, Arms & legs,
 * Holding), then the group within it (Everyday, Gems, Metals...). Below them
 * only that one group's choices show, as small tiles, so they fit in a row
 * or two and every change can be seen next to the buddy. Every part is free from the start (see SproutBuddy.tsx
 * for why).
 *
 * Shared with grown-up Settings (Change Sprout Buddy): `grownUp` there puts
 * every name in the grown-up's language; the boys' own screen stays English.
 *
 * Choosing what you look like in an app is one of the clearest ways software
 * can give a child a sense of choice, which the research on what keeps
 * children coming back puts first (engagement-and-reviews.md, R24).
 */
type Part = 'leaf' | 'color' | 'limbs' | 'held'

const PARTS: [Part, string][] = [
  // "Skin", not "Color" (Sam, 11 Oct): fire, a portal or gold are not colours.
  // Only the label changed; the saved code still calls this part `color`.
  ['leaf', 'Top'], ['color', 'Skin'], ['limbs', 'Arms & legs'], ['held', 'Holding'],
]

export function BuddyMaker({
  child, onSaved, onCancel, grownUp = false,
}: { child: Child; onSaved: (child: Child) => void; onCancel?: () => void; grownUp?: boolean }) {
  const tr = grownUp ? t : english
  const name = (value: string) => tr(LABELS[value] ?? value)
  const [buddy, setBuddy] = useState<Buddy>(parseBuddy(child.avatar_ref) ?? DEFAULT_BUDDY)
  const [part, setPart] = useState<Part>('leaf')
  // Which group is open in each part. Starts on the group holding the
  // buddy's current choice, so a child sees what they already have.
  const [group, setGroup] = useState<Record<Part, number>>(() => {
    const start = parseBuddy(child.avatar_ref) ?? DEFAULT_BUDDY
    const find = (gs: [string, string[]][], v: string) => Math.max(0, gs.findIndex(([, vs]) => vs.includes(v)))
    return {
      leaf: find(LEAF_GROUPS, start.leaf), color: find(COLOR_GROUPS, start.color),
      limbs: find(LIMB_GROUPS, start.limbs), held: 0,
    }
  })
  const narrow = useNarrow()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [bounce, setBounce] = useState(0)

  const choose = (next: Buddy) => {
    sfx.tap()
    setBuddy(next)
    setBounce((b) => b + 1)
  }

  const save = async () => {
    setBusy(true)
    setProblem(null)
    try {
      const { child: updated } = await api.setBuddy(child.learner_id, buddyCode(buddy))
      sfx.correct()
      onSaved(updated)
    } catch (err) {
      setProblem(tr((err as { detail?: string } | null)?.detail || 'That did not save. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const groups: [string, string[]][] =
    part === 'leaf' ? LEAF_GROUPS
    : part === 'color' ? COLOR_GROUPS
    : part === 'limbs' ? LIMB_GROUPS
    : [['', [...HELD]]]

  const open = Math.min(group[part], groups.length - 1)
  const [, options] = groups[open]!
  const subTabs = groups.length > 1

  return (
    <TraceRegion id="T2" writes note="saved as avatar_ref on learner, e.g. buddy:clover.mint.gardener.can">
    <div>
      <style>{`@keyframes ws-pop { 0% { transform: scale(0.92) } 60% { transform: scale(1.04) } 100% { transform: scale(1) } }
        .ws-buddy-pop { animation: ws-pop 0.35s ease-out; }
        @media (prefers-reduced-motion: reduce) { .ws-buddy-pop { animation: none } }`}</style>

      {/* The buddy, then the parts, then the groups within the part: one row.
          Its height is fixed, tall enough for the longest group list, and it
          always has three columns, so the buddy never moves when a tab is
          picked (Sam, 9 Oct: it slid down when Color opened six groups). */}
      <div style={{
        display: 'grid', gap: narrow ? 6 : 10, alignItems: 'start', height: ROW_HEIGHT,
        gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)',
      }}>
        <div style={{
          display: 'grid', placeItems: 'center', height: '100%',
          background: `radial-gradient(circle at 50% 55%, ${theme.greenPale}, transparent 70%)`,
          borderRadius: radius.xl,
        }}>
          <div key={bounce} className="ws-buddy-pop">
            <SproutBuddy buddy={buddy} size={narrow ? 128 : 178} mood="happy" label={tr("{name}'s Sprout Buddy", { name: child.display_name })} />
          </div>
        </div>

        <div role="tablist" aria-orientation="vertical" aria-label={tr('Parts')} style={column}>
          {PARTS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={part === key}
              onClick={() => { sfx.tap(); setPart(key) }}
              style={{ ...tab(part === key, 'part', narrow), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tr(label)}</span>
              {/* points at the column it opens */}
              {part === key && subTabs ? <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>›</span> : null}
            </button>
          ))}
        </div>

        {subTabs ? (
          <div role="tablist" aria-orientation="vertical" aria-label={tr('Groups')} style={column}>
            {groups.map(([heading], g) => (
              <button key={heading} type="button" role="tab" aria-selected={open === g}
                onClick={() => { sfx.tap(); setGroup((s) => ({ ...s, [part]: g })) }}
                style={tab(open === g, 'group', narrow)}>
                {tr(heading)}
              </button>
            ))}
          </div>
        ) : <div aria-hidden />}
      </div>

      {part === 'held' && !holdsSomething(buddy.limbs) && buddy.limbs !== 'none' ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: theme.muted }}>
          {tr('{pose} uses both hands. Picking something to hold changes the arms to {arms}, so a hand is free.',
            { pose: name(buddy.limbs), arms: name(armsFor(buddy.limbs, 'book')).toLowerCase() })}
        </p>
      ) : null}

      {/* Only the open group's choices, in one row. At least six columns, so a
          short group does not get giant tiles; on a phone a long group
          slides sideways rather than wrapping. */}
      <div role="tabpanel" style={{
        display: 'grid', gap: 5, marginTop: 10,
        gridTemplateColumns: `repeat(${Math.max(options.length, 6)}, minmax(${narrow ? 52 : 0}px, 1fr))`,
        overflowX: 'auto', paddingBottom: 2,
      }}>
        {options.map((value) => {
          // Holding something needs a free hand: the arms change to match.
          const next = (part === 'held'
            ? { ...buddy, held: value, limbs: armsFor(buddy.limbs, value) }
            : { ...buddy, [part]: value }) as Buddy
          const on = buddy[part] === value && (part !== 'held' || value === 'none' || holdsSomething(buddy.limbs))
          return (
            <button key={value} type="button" onClick={() => choose(next)} aria-pressed={on}
              aria-label={name(value)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                gap: 1, padding: '4px 1px 5px', borderRadius: radius.md, cursor: 'pointer', minWidth: 0,
                border: `2.5px solid ${on ? theme.orange : theme.borderSoft}`,
                background: on ? theme.orangePale : theme.card, fontFamily: 'inherit',
              }}>
              {part === 'color' ? (
                <span style={{
                  width: 30, height: 30, borderRadius: 999, margin: '4px 0 3px',
                  background: material(value).css ?? material(value).limbs,
                  border: '2.5px solid #2B1D14',
                }} />
              ) : (
                <SproutBuddy buddy={next} size={36} animate={false} label="" />
              )}
              {/* Up to two lines, never cut off ("Two leaves", "Fiddlehead"),
                  in a box of fixed height so every tile lines up. */}
              <span style={{
                fontSize: 10, fontWeight: 700, color: theme.text2, lineHeight: 1.1, maxWidth: '100%',
                height: '2.2em', display: 'flex', alignItems: 'center', textAlign: 'center',
                overflow: 'hidden',
              }}>
                {name(value)}
              </span>
            </button>
          )
        })}
      </div>

      {problem ? <p style={{ color: theme.red, fontSize: 15, margin: '10px 0 0' }}>{problem}</p> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Button tone="child" onClick={save} disabled={busy} style={{ flex: '2 1 160px' }}>
          {busy ? tr('Saving…') : tr('Save my buddy')}
        </Button>
        <Button tone="quiet" onClick={() => choose(randomBuddy())} style={{ flex: '1 1 120px' }}>
          🎲 {tr('Surprise me')}
        </Button>
        {onCancel ? <Button tone="ghost" onClick={onCancel} style={{ flex: '1 1 100px' }}>{tr('Back')}</Button> : null}
      </div>
    </div>
    </TraceRegion>
  )
}

const column: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }

// Six group tabs (the most any part has: Skin) at 34px, with their gaps.
const ROW_HEIGHT = 6 * 34 + 5 * 5

/** A vertical tab. Parts are green, like the app's other tabs; groups orange, like the choices they open. */
function tab(on: boolean, level: 'part' | 'group', narrow: boolean): CSSProperties {
  const [line, fill, ink] = level === 'part'
    ? [theme.green, theme.greenPale, theme.greenDarker]
    : [theme.orange, theme.orangePale, theme.orangeDark]
  return {
    width: '100%', minHeight: level === 'part' ? TAP_MIN : 34, padding: narrow ? '4px 6px' : '4px 10px',
    borderRadius: radius.md, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
    border: `2px solid ${on ? line : theme.borderSoft}`,
    background: on ? fill : theme.card,
    color: on ? ink : theme.text2,
    fontWeight: on ? 800 : 700, fontSize: narrow ? 12.5 : 14, lineHeight: 1.15,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }
}

/** True on a phone-width screen, where everything is drawn a size smaller. */
function useNarrow(): boolean {
  const query = '(max-width: 460px)'
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const m = window.matchMedia(query)
    const on = () => setNarrow(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return narrow
}
